import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Lock, Loader2 } from "lucide-react";
import { useCart, formatNZD } from "@/lib/cart";
import { createCheckoutSession } from "@/lib/api";
import { toast } from "sonner";

const SHIPPING_OPTIONS = [
  { id: "standard", label: "NZ Standard (Track & Trace)", price: 25.0 },
  { id: "rural", label: "NZ Rural (Track & Trace)", price: 35.0 },
];

export default function CartPage() {
  const { items, subtotal, setQty, removeItem } = useCart();
  const [shipping, setShipping] = useState("standard");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const shippingObj = SHIPPING_OPTIONS.find((s) => s.id === shipping);
  const total = subtotal + (shippingObj?.price || 0);

  const handleCheckout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          slug: i.slug,
          quantity: i.quantity,
          addons: i.addonIds,
        })),
        shipping_method: shipping,
        customer_email: email || undefined,
      };
      const res = await createCheckoutSession(payload);
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setLoading(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-[800px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24 text-center">
        <ShoppingBag size={32} className="text-[#6F4E37] mx-auto mb-6" />
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight">
          Your cart is empty.
        </h1>
        <p className="mt-6 text-[#2C2421]/75 leading-relaxed text-lg">
          Browse the workshop's pieces and add something handmade to your bench.
        </p>
        <Link
          to="/shop"
          data-testid="cart-empty-shop-btn"
          className="mt-10 inline-flex items-center gap-3 bg-[#2C2421] text-[#FDFBF7] px-7 py-4 text-sm font-medium hover:bg-[#4A3C38] transition-colors"
        >
          Shop the collection
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-[1200px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24">
      <header className="mb-12">
        <div className="eyebrow mb-4">Your cart</div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight">
          Review &amp; <span className="italic">check out</span>.
        </h1>
      </header>

      <div className="grid lg:grid-cols-12 gap-12">
        {/* Items */}
        <div className="lg:col-span-8">
          <div className="border-t border-[#E5E0D8]">
            {items.map((it) => (
              <div
                key={it.key}
                data-testid={`cart-row-${it.slug}`}
                className="grid grid-cols-12 gap-4 py-6 border-b border-[#E5E0D8]"
              >
                <Link
                  to={`/shop/${it.slug}`}
                  className="col-span-4 sm:col-span-2 aspect-square bg-[#F5F2EB] overflow-hidden block"
                >
                  <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                </Link>
                <div className="col-span-8 sm:col-span-7 flex flex-col justify-between">
                  <div>
                    <Link to={`/shop/${it.slug}`} className="font-serif text-xl link-underline">
                      {it.name}
                    </Link>
                    {it.addons.length > 0 && (
                      <ul className="mt-1 text-xs text-[#6F4E37]">
                        {it.addons.map((a) => (
                          <li key={a.id}>+ {a.label} ({formatNZD(a.price)})</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      data-testid={`cart-dec-${it.slug}`}
                      onClick={() => setQty(it.key, it.quantity - 1)}
                      className="h-8 w-8 border border-[#E5E0D8] inline-flex items-center justify-center hover:border-[#2C2421]"
                      aria-label="Decrease"
                    >
                      <Minus size={14} />
                    </button>
                    <span data-testid={`cart-qty-${it.slug}`} className="min-w-[2ch] text-center text-sm">
                      {it.quantity}
                    </span>
                    <button
                      data-testid={`cart-inc-${it.slug}`}
                      onClick={() => setQty(it.key, it.quantity + 1)}
                      className="h-8 w-8 border border-[#E5E0D8] inline-flex items-center justify-center hover:border-[#2C2421]"
                      aria-label="Increase"
                    >
                      <Plus size={14} />
                    </button>
                    <button
                      data-testid={`cart-remove-${it.slug}`}
                      onClick={() => removeItem(it.key)}
                      className="ml-3 text-xs text-[#6F4E37] hover:text-[#2C2421] inline-flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </div>
                <div className="col-span-12 sm:col-span-3 sm:text-right font-serif text-xl text-[#2C2421]">
                  {formatNZD(it.unit_price * it.quantity)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-4">
          <div className="bg-[#F5F2EB] border border-[#E5E0D8] p-6 md:p-8 lg:sticky lg:top-28">
            <div className="eyebrow mb-4">Shipping</div>
            <div className="space-y-2">
              {SHIPPING_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  data-testid={`shipping-${opt.id}`}
                  className={`flex items-center justify-between p-3 border cursor-pointer transition-colors ${
                    shipping === opt.id
                      ? "border-[#2C2421] bg-[#FDFBF7]"
                      : "border-[#E5E0D8] hover:border-[#A89F91]"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="shipping"
                      value={opt.id}
                      checked={shipping === opt.id}
                      onChange={() => setShipping(opt.id)}
                      className="accent-[#2C2421]"
                    />
                    <span className="text-sm">{opt.label}</span>
                  </span>
                  <span className="text-sm font-medium">{formatNZD(opt.price)}</span>
                </label>
              ))}
            </div>

            <div className="eyebrow mt-6 mb-2">Email (for receipt)</div>
            <input
              data-testid="cart-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-transparent border-0 border-b border-[#A89F91]/50 focus:outline-none focus:border-[#2C2421] py-2 text-sm"
            />

            <div className="mt-6 pt-4 border-t border-[#E5E0D8] space-y-2 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="text-[#6F4E37]">Subtotal</span>
                <span data-testid="cart-subtotal">{formatNZD(subtotal)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-[#6F4E37]">Shipping</span>
                <span data-testid="cart-shipping">{formatNZD(shippingObj.price)}</span>
              </div>
              <div className="flex items-baseline justify-between pt-3 mt-2 border-t border-[#E5E0D8]">
                <span className="eyebrow !text-[#A89F91]">Total (NZD)</span>
                <span data-testid="cart-total" className="font-serif text-2xl text-[#2C2421]">{formatNZD(total)}</span>
              </div>
            </div>

            <button
              data-testid="cart-checkout-btn"
              onClick={handleCheckout}
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#2C2421] text-[#FDFBF7] py-4 text-sm font-medium hover:bg-[#4A3C38] transition-colors disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 size={14} className="animate-spin" /> Redirecting…</>
              ) : (
                <><Lock size={14} /> Pay securely with Stripe</>
              )}
            </button>

            <p className="mt-3 text-xs text-[#6F4E37] leading-relaxed">
              Cards &amp; Afterpay accepted (where enabled). You'll be redirected to Stripe to complete payment.
            </p>
            <Link
              to="/shop"
              data-testid="cart-continue-shopping"
              className="block mt-4 text-center text-sm link-underline text-[#6F4E37]"
            >
              Continue shopping
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
